import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/core/api/axios-client";
import type {
  NotificationResponse,
  NotificationType,
} from "@/core/api/types/notifications";
import toast from "react-hot-toast";

const NOTIFICATIONS_QUERY_KEY = "notifications";
const MESSAGES_QUERY_KEY = "messages";
const UNREAD_COUNT_QUERY_KEY = "unread-count";

interface UseNotificationsOptions {
  enabled?: boolean;
}

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const queryClient = useQueryClient();
  const { enabled = true } = options;

  // Fetch notifications
  const {
    data: notificationsData,
    isLoading: isLoadingNotifications,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<NotificationResponse>(
        "/notifications?type=NOTIFICATION"
      );
      return response.data;
    },
    enabled,
  });

  // Fetch messages
  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: [MESSAGES_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<NotificationResponse>(
        "/notifications?type=MESSAGE"
      );
      return response.data;
    },
    enabled,
  });

  // Mark single notification as read
  const markAsRead = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiClient.post(
        `/notifications/${notificationId}/read`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MESSAGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Errore durante l'operazione"
      );
    },
  });

  // Mark all notifications as read
  const markAllAsRead = useMutation({
    mutationFn: async (type?: NotificationType) => {
      const response = await apiClient.post("/notifications/read-all", {
        type,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MESSAGES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Errore durante l'operazione"
      );
    },
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [MESSAGES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
  };

  return {
    notifications: notificationsData?.data || [],
    messages: messagesData?.data || [],
    isLoading: isLoadingNotifications || isLoadingMessages,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    invalidateAll,
    refetchAll: () => {
      refetchNotifications();
      refetchMessages();
    },
  };
};
